import {loggedInEmployeeAtom} from 'atoms/employee';
import type {WorkstreamBasicWithSummary} from 'client';
import {useAtomValue} from 'jotai';
import {useMemo} from 'react';
import {useWorkstreams} from 'views/workstreams/detail/hooks';
import type {AltitudeFilterValue, WorkstreamFilters} from 'views/workstreams/detail/types';
import {
  getDefaultWorkstreamTimeframe,
  narrativePlanningHalfToTimeframeTableFilter,
} from 'views/workstreams/detail/workstreamTimeframe';

/** What a suggestion is about, for analytics (never the question text). */
export type AskSuggestionKind =
  | 'workstream'
  | 'workstream_update'
  | 'hr'
  | 'owner'
  | 'it'
  | 'headcount';

export type AskSuggestion = {
  question: string;
  kind: AskSuggestionKind;
};

type SuggestionWorkstream = Pick<
  WorkstreamBasicWithSummary,
  'name' | 'status_update_needed' | 'updates'
>;

const OFF_TRACK = new Set(['off_track', 'at_risk']);

/**
 * A question about one workstream only when something needs you: an update
 * due, or one at risk or off track. People almost never ask Ask about their own
 * work unprompted, so there's no catch-up question when nothing does.
 */
const workstreamSuggestion = (workstreams: SuggestionWorkstream[]): AskSuggestion | null => {
  const due = workstreams.find((ws) => Boolean(ws.status_update_needed));
  if (due) {
    return {question: `Draft my status update for ${due.name}`, kind: 'workstream_update'};
  }
  const offTrack = workstreams.find((ws) => OFF_TRACK.has(ws.updates?.[0]?.status ?? ''));
  if (offTrack) {
    return {question: `What's the latest on ${offTrack.name}?`, kind: 'workstream'};
  }
  return null;
};

/**
 * Questions shaped like the ones people type into Ask most (HR, service
 * owners, IT, headcount), each one Ask answers in a line or two from Knowledge
 * or the directory.
 */
const COMMON_SUGGESTIONS: AskSuggestion[] = [
  {question: 'Who is my HRBP?', kind: 'hr'},
  {question: 'Who owns Slack?', kind: 'owner'},
  {question: 'How do I get a new laptop?', kind: 'it'},
  {question: 'How many engineers are at Dropbox?', kind: 'headcount'},
];

const SUGGESTION_COUNT = 4;

/**
 * The Ask suggestions: a workstream that needs you, if one does, then the
 * common questions.
 */
export const buildAskSuggestions = ({
  workstreams,
}: {
  workstreams: SuggestionWorkstream[];
}): AskSuggestion[] => {
  const workstream = workstreamSuggestion(workstreams);
  return [...(workstream ? [workstream] : []), ...COMMON_SUGGESTIONS].slice(0, SUGGESTION_COUNT);
};

const currentHalfFilters = (): WorkstreamFilters => ({
  altitude: ['false'] as AltitudeFilterValue[],
  timeframes: [
    narrativePlanningHalfToTimeframeTableFilter(getDefaultWorkstreamTimeframe()),
    'enablement',
  ],
});

/**
 * The asker's Ask suggestions. The workstream list is the profile card's
 * request (current half + Enablement), so it's often already cached; until it
 * arrives there's no workstream question.
 */
export const useAskSuggestions = ({enabled = true}: {enabled?: boolean} = {}): AskSuggestion[] => {
  const ldap = useAtomValue(loggedInEmployeeAtom)?.employee?.ldap;
  const filters = useMemo(currentHalfFilters, []);
  const query = useWorkstreams({
    scope_type: 'person',
    scope_value: ldap,
    filters,
    group_by: 'none',
    enabled: enabled && Boolean(ldap),
  });
  const workstreams = query.data?.workstreams as WorkstreamBasicWithSummary[] | undefined;

  return useMemo(() => buildAskSuggestions({workstreams: workstreams ?? []}), [workstreams]);
};
